/**
 * Purpose: Provides a popup dialog to encourage users to enable web push notifications
 *
 * How it works:
 * 1. Shows automatically after a delay for logged-in users who haven't subscribed yet
 * 2. Respects user preferences with "don't remind again" option
 * 3. Implements time-based reminder logic to avoid excessive prompting
 * 4. Integrates with useWebPush hook for subscription handling
 * 5. Conditionally renders based on device/environment capabilities
 *
 * Key features:
 * - Time-based display rules (initial delay, reminder intervals)
 * - Environment detection for iOS and PWA status
 * - Persistent user preferences via localStorage
 * - Localized content through i18n
 * - Accessibility support through primitives components
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/primitives/dialog";

import { useState, useEffect } from "react";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useWebPush } from "./useWebPush";
import { WebPushSwitch } from "./WebPushSwitch";
import { Checkbox } from "@/components/primitives/checkbox";
import { useTranslation } from "react-i18next";
import useAuthSession from "@/middleware/hooks/useAuthSession";
import { IS_IOS, IS_PWA } from "@/lib/environmentDetector";
import { useAnnouncementSlot } from "@/zustand/AnnouncementState";

const WEB_PUSH_STORAGE_KEYS = {
  DONT_REMIND: "webPushDontRemind",
  LAST_SHOWN: "webPushLastShown",
} as const;

const TIME_DELAYS = {
  INITIAL_SHOW_DELAY: 10000,
  REMINDER_INTERVAL_HOURS: 24,
} as const;

const WebPushPopUp: React.FC = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { isSubscribed } = useWebPush();
  const { isLoggedIn } = useAuthSession();
  // Participate in the announcement queue only once logged in, so this
  // globally-mounted popup can't lock in the boot winner on the login page.
  const hasAnnouncementSlot = useAnnouncementSlot("web-push", isLoggedIn);

  const [webPushDontRemind, setWebPushDontRemind] = useLocalStorage(
    WEB_PUSH_STORAGE_KEYS.DONT_REMIND,
    false,
  );
  const [lastShownTimestamp, setLastShownTimestamp] = useLocalStorage<number>(
    WEB_PUSH_STORAGE_KEYS.LAST_SHOWN,
    0,
  );

  useEffect(() => {
    if (!hasAnnouncementSlot) {
      return;
    }
    if (!isLoggedIn) {
      return;
    }
    if (isSubscribed) {
      return;
    }
    if (webPushDontRemind) {
      return;
    }
    if (IS_IOS && !IS_PWA) {
      return;
    }
    const seenRecently =
      (Date.now() - lastShownTimestamp) / (1000 * 60 * 60) <
      TIME_DELAYS.REMINDER_INTERVAL_HOURS;
    if (seenRecently) {
      return;
    }
    const timer = setTimeout(
      () => setOpen(true),
      TIME_DELAYS.INITIAL_SHOW_DELAY,
    );
    return () => clearTimeout(timer);
  }, [
    isSubscribed,
    webPushDontRemind,
    lastShownTimestamp,
    isLoggedIn,
    hasAnnouncementSlot,
  ]);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen && !webPushDontRemind) {
          setLastShownTimestamp(Date.now());
        }
        setOpen(isOpen);
      }}
    >
      <DialogContent className="w-[320px]">
        <DialogTitle>{t("webPushTitle")}</DialogTitle>
        <DialogDescription>{t("webPushDescription")}</DialogDescription>
        <WebPushSwitch />

        <div className="mt-2 flex items-center space-x-2">
          <Checkbox
            id="dont-remind"
            checked={webPushDontRemind}
            onCheckedChange={(checked) => setWebPushDontRemind(!!checked)}
          />
          <label
            htmlFor="dont-remind"
            className="text-sm text-muted-foreground"
          >
            {t("dontRemindAgain")}
          </label>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WebPushPopUp;
